// Costs passed along to a client (TD-088, TD-100): the markup as a percentage or a fixed amount and the form that sets it.
// A cost names its client alone (finance consolidation answer d): it rides the client's next invoice. Pure, so it can be tested.
import { formatMoney } from "@/utils/currency";
import type { PassAlong, PassBilling, PassInput } from "../types";
import { pctText } from "./format";

/** 1000% at most, as the server takes it (passed_costs.MARKUP_BP_MAX): more is a typo, not a markup. */
export const MARKUP_PCT_MAX = 1000;
const MARKUP_BP_MAX = MARKUP_PCT_MAX * 100;

/** "at cost", "+7,5% markup" or "+15,00 € markup". */
export function billingText(p: Pick<PassAlong, "billing" | "markupBp" | "markupMinor">, currency = "EUR"): string {
  if (p.billing === "percent") return `+${pctText((p.markupBp ?? 0) / 100)}% markup`;
  if (p.billing === "fixed") return `+${formatMoney(p.markupMinor ?? 0, currency)} markup`;
  return "at cost";
}

export const capitalized = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** How an expense or a bill is passed along, in one line: "+10% markup"; "Social, at cost" on a row main passed along to a contract. */
export const passAlongText = (p: PassAlong): string => (p.contractName ? `${p.contractName}, ${billingText(p)}` : capitalized(billingText(p)));

// ---- the form

export interface PassForm {
  on: boolean;
  billing: PassBilling;
  /** A percentage markup in percent, as PercentInput holds it: 7.5 is 7,5%. */
  pct: number;
  /** A fixed markup in minor units. */
  fixed: number | null;
}

export const NO_PASS: PassForm = { on: false, billing: "at_cost", pct: 0, fixed: null };

/** A saved passing along, as the form edits it. */
export function passForm(p: PassAlong | null): PassForm {
  if (!p) return NO_PASS;
  return {
    on: true,
    billing: p.billing,
    pct: p.billing === "percent" && p.markupBp !== null ? p.markupBp / 100 : 0,
    fixed: p.billing === "fixed" ? p.markupMinor : null,
  };
}

/** Passed along only with a client: a cleared client passes nothing along, whatever the box said before. */
const passOn = (p: PassForm, clientId: string): boolean => p.on && !!clientId;

/** The passing along the checks and the request read: off for the agency's own client, whatever the box said before. Its
 * profile is never billed, so the cost stays the agency's own expense (TD-105, the server's PASS_OWN). */
export const livePass = (p: PassForm, own: boolean): PassForm => (own ? { ...p, on: false } : p);

export interface PassCheck {
  /** Under the markup, its own field. */
  markupError: string | undefined;
  blocked: boolean;
}

/** What blocks saving: a markup it cannot read, under its own field. Nothing blocks without a client: nothing is passed along then. */
export function passCheck(p: PassForm, clientId: string): PassCheck {
  if (!passOn(p, clientId)) return { markupError: undefined, blocked: false };
  const bp = Math.round(p.pct * 100);
  const markupError =
    p.billing === "percent" && (bp < 1 || bp > MARKUP_BP_MAX)
      ? `Enter the markup, from ${pctText(0.01)} to ${pctText(MARKUP_PCT_MAX)}%`
      : p.billing === "fixed" && (!p.fixed || p.fixed <= 0)
        ? "Enter the markup amount"
        : undefined;
  return { markupError, blocked: !!markupError };
}

/** The request's fields: how it is billed, or nulls for the agency's own cost. */
export function passInput(p: PassForm, clientId: string): PassInput {
  if (!passOn(p, clientId)) return { passBilling: null, markupBp: null, markupMinor: null };
  return {
    passBilling: p.billing,
    markupBp: p.billing === "percent" ? Math.round(p.pct * 100) : null,
    markupMinor: p.billing === "fixed" ? p.fixed : null,
  };
}
